import type { Editor } from '@tiptap/core';
import type { Fragment, Node as PmNode } from '@tiptap/pm/model';
import { TextSelection, type EditorState } from '@tiptap/pm/state';
import { Node, mergeAttributes } from '@tiptap/react';

function findZydefTermAtSelection(state: any): { pos: number; node: any } | null {
    const { $from, $to } = state.selection;

    // Ancestor walk from $from (covers cursor/anchor inside term text).
    for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node?.type?.name === 'zydefTerm') {
            return { pos: $from.before(depth), node };
        }
    }

    // Ancestor walk from $to (covers selection where focus end is inside term).
    for (let depth = $to.depth; depth > 0; depth--) {
        const node = $to.node(depth);
        if (node?.type?.name === 'zydefTerm') {
            return { pos: $to.before(depth), node };
        }
    }

    // Cursor sitting immediately after a zydefTerm (boundary position in parent).
    const nodeBefore = $from.nodeBefore;
    if (nodeBefore?.type?.name === 'zydefTerm') {
        return { pos: $from.pos - nodeBefore.nodeSize, node: nodeBefore };
    }

    // NodeSelection directly on a zydefTerm (selectable: true lets this happen).
    const selectedNode = (state.selection as any).node;
    if (selectedNode?.type?.name === 'zydefTerm') {
        return { pos: state.selection.from, node: selectedNode };
    }

    return null;
}

function hasAllowedZydefnAncestor(state: any, pos: number): boolean {
    const $pos = state.doc.resolve(pos);
    for (let depth = $pos.depth; depth > 0; depth--) {
        const name = $pos.node(depth)?.type?.name;
        if (name === 'zydefn' || name === 'zydefnBlock') {
            return true;
        }
    }
    return false;
}

function selectionInsideAllowedZydefn(state: any): boolean {
    const { from, to } = state.selection;
    return hasAllowedZydefnAncestor(state, from) && hasAllowedZydefnAncestor(state, to);
}

/**
 * `zydefTerm` allows text and `inlineMath` only (not arbitrary inlines such as `zydefn`), so the
 * replacement slice must be expressible in that content model.
 */
function isZydefTermSliceContentAllowed(content: Fragment): boolean {
    let disallowed = false;
    content.descendants((node: PmNode) => {
        if (node.isText) {
            return true;
        }

        // return false so we dont walk the inlineMath tree
        if (node.type.name === 'inlineMath') {
            return false;
        }

        // we have come across something that is not text | inlineMath
        disallowed = true;
        return false;
    });
    return !disallowed;
}

/**
 * A `zydefTerm` has substance when it has any `inlineMath` or any non–whitespace text after stripping
 * U+200B placeholders.
 */
export function zydefTermNodeHasSubstance(node: PmNode): boolean {
    if (node.type.name !== 'zydefTerm') {
        return false;
    }
    let text = '';
    let hasMath = false;
    node.forEach((child: PmNode) => {
        if (child.isText) {
            text += child.text ?? '';
        } else if (child.type.name === 'inlineMath') {
            hasMath = true;
        }
    });
    if (hasMath) {
        return true;
    }
    return text.replaceAll('\u200B', '').trim().length > 0;
}

/**
 * A term must be non-trivial: non-whitespace text and/or at least one `inlineMath` node.
 */
function hasZydefTermSubstance(state: EditorState): boolean {
    const { from, to } = state.selection;
    if (state.doc.textBetween(from, to, '').replaceAll('\u200B', '').trim().length) {
        return true;
    }
    let hasMath = false;
    state.doc.nodesBetween(from, to, (node: PmNode) => {
        if (node.type.name === 'inlineMath') {
            hasMath = true;
        }
    });
    return hasMath;
}

export interface ZyDefnTermAttributes {
    obeycase: boolean;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zydefTerm: {
            setZydefTerm: () => ReturnType;
            unsetZydefTerm: () => ReturnType;
            toggleZydefTerm: () => ReturnType;
        };
    }
}

export const ZyDefnTerm = Node.create({
    name: 'zydefTerm',

    group: 'inline',

    inline: true,

    selectable: true,

    content: '(text | inlineMath)*',

    addAttributes() {
        return {
            obeycase: {
                default: false,
                parseHTML: element => {
                    if (!(element instanceof HTMLElement)) {
                        return false;
                    }
                    return element.getAttribute('obeycase') === 'true';
                },
                renderHTML: () => ({
                    obeycase: 'true',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span.term[data-term="true"]',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) return false;
                    if (element.getAttribute('data-term') !== 'true') return false;
                    if (!element.closest('span[data-definition="true"]')) return false;
                    return {};
                },
            },
            {
                tag: 'span.term[data-zydefn-term="true"][data-term="true"]',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) return false;
                    if (element.getAttribute('data-term') !== 'true') return false;
                    if (element.getAttribute('data-zydefn-term') !== 'true') return false;
                    if (element.closest('span[data-definition="true"]')) return false;
                    return {};
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                class: 'term zydefn-term',
                'data-term': 'true',
                'data-zydefn-term': 'true',
            }),
            0,
        ];
    },

    addKeyboardShortcuts() {
        return {
            'Mod-Alt-t': ({ editor }: { editor: Editor }) => editor.commands.toggleZydefTerm(),
        };
    },

    addCommands() {
        return {
            setZydefTerm:
                () =>
                ({ state, dispatch }) => {
                    const { selection, schema } = state;
                    const { from, to, $from, $to, empty } = selection;
                    if (empty) return false;

                    if (!selectionInsideAllowedZydefn(state)) return false;

                    // Must remain within a single parent for safe replacement.
                    if (!$from.sameParent($to)) return false;

                    // Works for both:
                    // - zydefnBlock (selection parent is usually a paragraph textblock)
                    // - inline zydefn (selection parent is the inline `zydefn` node itself)
                    const parent = $from.parent;
                    const parentIsAllowed = parent.isTextblock || parent.type?.name === 'zydefn';
                    if (!parentIsAllowed) return false;

                    const slice = state.doc.slice(from, to);
                    if (!isZydefTermSliceContentAllowed(slice.content)) {
                        return false;
                    }
                    if (!hasZydefTermSubstance(state)) {
                        return false;
                    }

                    const type = schema.nodes.zydefTerm;
                    if (!type) return false;

                    // Preserve marks by keeping the actual text slice content.
                    const termNode = type.create({ obeycase: true }, slice.content);

                    const tr = state.tr.replaceRangeWith(from, to, termNode);
                    const endAfterTerm = from + termNode.nodeSize;
                    tr.setSelection(TextSelection.create(tr.doc, Math.min(endAfterTerm, tr.doc.content.size)));
                    dispatch?.(tr.scrollIntoView());
                    return true;
                },

            unsetZydefTerm:
                () =>
                ({ state, dispatch }) => {
                    const found = findZydefTermAtSelection(state);
                    if (!found) return false;
                    const { pos, node } = found;

                    const tr = state.tr.replaceWith(pos, pos + node.nodeSize, node.content);
                    const endOfUnwrapped = pos + node.content.size;
                    tr.setSelection(TextSelection.create(tr.doc, Math.min(endOfUnwrapped, tr.doc.content.size)));
                    dispatch?.(tr.scrollIntoView());
                    return true;
                },

            toggleZydefTerm:
                () =>
                ({ editor, state, dispatch }) => {
                    const { selection } = state;
                    const { $from, empty } = selection;

                    let insideZydefn = false;
                    for (let depth = $from.depth; depth > 0; depth--) {
                        const name = $from.node(depth)?.type?.name;
                        if (name === 'zydefn' || name === 'zydefnBlock') {
                            insideZydefn = true;
                            break;
                        }
                    }
                    if (!insideZydefn) return false;

                    if (!empty && findZydefTermAtSelection(state)) {
                        return editor.commands.unsetZydefTerm();
                    }

                    if (!empty) {
                        return editor.commands.setZydefTerm();
                    }

                    const insideOfZydefTerm = findZydefTermAtSelection(state);
                    if (insideOfZydefTerm) {
                        const { pos, node } = insideOfZydefTerm;
                        const exitPos = pos + node.nodeSize;
                        const tr = state.tr.insertText('\u200B', exitPos);
                        tr.setSelection(TextSelection.create(tr.doc, exitPos + 1));
                        dispatch?.(tr);
                        editor.commands.focus();
                        return true;
                    }

                    const pos = selection.from;
                    // Zero width space (u200B) is required or else the term styling does not get applied when text is empty
                    const emptyTerm = state.schema.nodes.zydefTerm.create(null, state.schema.text('\u200B'));
                    const tr = state.tr.replaceRangeWith(pos, pos, emptyTerm);
                    tr.setSelection(TextSelection.create(tr.doc, pos + emptyTerm.nodeSize));
                    tr.scrollIntoView();
                    dispatch?.(tr);
                    editor.commands.focus();
                    return true;
                },
        };
    },
});

export default ZyDefnTerm;
